/**
 * NotesDialog — Modal "Crear/Editar Nota"
 *
 * Extraído de App.js líneas 9464-9555 durante la Ola D del refactor 2026-02.
 * Maneja creación y edición de notas con tipo, prioridad, contenido y visibilidad.
 * Estado y handlers se inyectan por props para preservar el comportamiento.
 */

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Label } from './ui/label';
import { Button } from './ui/button';

const DEFAULT_NOTE = {
  content: '',
  visible_to_client: false,
  tipo: 'general',
  priority: 'normal',
};

export const NotesDialog = ({
  isOpen,
  onOpenChange,
  currentNote,
  setCurrentNote,
  editingNoteId,
  setEditingNoteId,
  onCreate,
  onUpdate,
}) => {
  const handleCancel = () => {
    onOpenChange(false);
    setEditingNoteId(null);
    setCurrentNote(DEFAULT_NOTE);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-slate-800 border-slate-700 text-white w-[95vw] max-w-2xl"
        data-testid="notes-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-xl text-white">
            {editingNoteId ? '✏️ Editar Nota' : '📝 Nueva Nota'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="note-tipo" className="text-slate-300">Tipo de Nota</Label>
            <select
              id="note-tipo"
              value={currentNote.tipo}
              onChange={(e) => setCurrentNote({ ...currentNote, tipo: e.target.value })}
              className="w-full bg-slate-900 border-slate-700 text-white rounded px-3 py-2"
              data-testid="note-tipo-select"
            >
              <option value="general">📋 General</option>
              <option value="logistica">📦 Logística</option>
              <option value="musical">🎵 Musical</option>
              <option value="tecnica">⚙️ Técnica</option>
            </select>
          </div>

          <div>
            <Label htmlFor="note-priority" className="text-slate-300">Prioridad</Label>
            <select
              id="note-priority"
              value={currentNote.priority}
              onChange={(e) => setCurrentNote({ ...currentNote, priority: e.target.value })}
              className="w-full bg-slate-900 border-slate-700 text-white rounded px-3 py-2"
              data-testid="note-priority-select"
            >
              <option value="low">Baja</option>
              <option value="normal">Normal</option>
              <option value="high">⚠️ Alta</option>
              <option value="urgent">🚨 Urgente</option>
            </select>
          </div>

          <div>
            <Label htmlFor="note-content" className="text-slate-300">Contenido</Label>
            <textarea
              id="note-content"
              value={currentNote.content}
              onChange={(e) => setCurrentNote({ ...currentNote, content: e.target.value })}
              className="w-full bg-slate-900 border-slate-700 text-white rounded px-3 py-2 min-h-[150px]"
              placeholder="Escribe tu nota aquí..."
              data-testid="note-content-input"
            />
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="note-visible"
              checked={currentNote.visible_to_client}
              onChange={(e) =>
                setCurrentNote({ ...currentNote, visible_to_client: e.target.checked })
              }
              className="w-4 h-4 rounded bg-slate-900 border-slate-700"
            />
            <Label htmlFor="note-visible" className="text-slate-300 cursor-pointer">
              👁️ Visible para el cliente
            </Label>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-600">
            <Button
              onClick={handleCancel}
              variant="outline"
              className="border-slate-600 text-white hover:bg-slate-700"
              data-testid="note-cancel-btn"
            >
              Cancelar
            </Button>
            <Button
              onClick={editingNoteId ? onUpdate : onCreate}
              className="bg-sky-600 hover:bg-sky-700 text-white"
              disabled={!currentNote.content.trim()}
              data-testid="note-submit-btn"
            >
              {editingNoteId ? 'Actualizar Nota' : 'Crear Nota'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NotesDialog;
